import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import Box from '../Box/Box';
import Button from '../Button/Button';
import { Icon } from '../Icon';
import IconLabel from '../IconLabel/IconLabel';
import { MarkdownContent } from '../MarkdownContent';
import { Heading, Text } from '../Typography';
import {
  actionsStyles,
  errorRetryStyles,
  footerStyles,
  getBannerStyles,
} from './AIResponse.styles';
import {
  type ContactLandownerButtonProps,
  ContactLandownerButton,
} from './components/ContactLandownerButton/ContactLandownerButton';
import { TopMatchingFieldNote } from './components/TopMatchingFieldNote/TopMatchingFieldNote';
import { TopMatchingReview } from './components/TopMatchingReview/TopMatchingReview';

export type AIResponseProps = {
  /**
   * Optional title for the AI response
   */
  title?: string;
  /**
   * Whether to show the title heading
   * @default true
   */
  showTitle?: boolean;
  /**
   * Whether to show disclaimer text
   */
  showDisclaimer?: boolean;
  /**
   * Whether to show helpful feedback question
   */
  showHelpfulQuestion?: boolean;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Content to display in the response
   */
  children?: React.ReactNode;
  /**
   * Callback when user clicks "Yes" for helpful feedback
   */
  onHelpfulYes?: () => void;
  /**
   * Callback when user clicks "No" for helpful feedback
   */
  onHelpfulNo?: () => void;
  /**
   * Visual variant of the response
   */
  variant?: 'default' | 'error' | 'success';
  /**
   * Callback for retry button in error state
   */
  onErrorRetry?: () => void;
  /**
   * Debounce time for helpful feedback in milliseconds
   */
  helpfulDebounceMs?: number;
  /**
   * Enable markdown rendering for children content.
   * When true, children should be a string containing markdown.
   * @default false
   */
  markdown?: boolean;
  /**
   * Whether to enable code copy functionality in markdown code blocks
   * @default true
   */
  enableCodeCopy?: boolean;
  /**
   * Callback fired when a ContactLandownerButton is displayed in the response.
   * Useful for analytics tracking (e.g., GTM events).
   */
  onContactLandownerDisplay?: () => void;
  /**
   * Callback fired when a ContactLandownerButton is clicked.
   * Useful for analytics tracking (e.g., GTM events).
   */
  onContactLandownerClick?: () => void;
};

const AIResponse = ({
  title = `Here's what I found`,
  showTitle = true,
  showDisclaimer = true,
  showHelpfulQuestion = true,
  className,
  children,
  onHelpfulYes,
  onHelpfulNo,
  variant = 'default',
  onErrorRetry,
  helpfulDebounceMs = 1000,
  markdown = false,
  enableCodeCopy = true,
  onContactLandownerDisplay,
  onContactLandownerClick,
}: AIResponseProps) => {
  const [visualHelpful, setVisualHelpful] = useState<'yes' | 'no' | undefined>(
    undefined
  );
  const debounceTimerRef = useRef<number | null>(null);
  const DEBOUNCE_MS = helpfulDebounceMs;

  // Create a wrapped ContactLandownerButton that includes analytics callbacks
  const WrappedContactLandownerButton = useCallback(
    (props: Omit<ContactLandownerButtonProps, 'onDisplay' | 'onClick'>) => (
      <ContactLandownerButton
        {...props}
        onDisplay={onContactLandownerDisplay}
        onClick={onContactLandownerClick}
      />
    ),
    [onContactLandownerClick, onContactLandownerDisplay]
  );

  // Memoize custom components to prevent unnecessary re-renders
  const customComponents = useMemo(
    () => ({
      TopMatchingFieldNote,
      TopMatchingReview,
      ContactLandownerButton: WrappedContactLandownerButton,
    }),
    [WrappedContactLandownerButton]
  );

  const scheduleCallback = (next: 'yes' | 'no' | undefined) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (next === 'yes' || next === 'no') {
      debounceTimerRef.current = window.setTimeout(() => {
        if (next === 'yes') onHelpfulYes?.();
        else onHelpfulNo?.();
        debounceTimerRef.current = null;
      }, DEBOUNCE_MS);
    }
  };

  const handleHelpfulYes = () => {
    const next = visualHelpful === 'yes' ? undefined : 'yes';
    setVisualHelpful(next);
    scheduleCallback(next);
  };

  const handleHelpfulNo = () => {
    const next = visualHelpful === 'no' ? undefined : 'no';
    setVisualHelpful(next);
    scheduleCallback(next);
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <Box
      className={className}
      display="flex"
      flexDirection="column"
      gap="var(--spacing-4)"
    >
      {variant !== 'error' && showTitle && (
        <Heading size="2xs" fontWeight="bold" color="var(--text-primary)">
          {title}
        </Heading>
      )}

      <div css={getBannerStyles(variant)}>
        {variant === 'error' ? (
          <Text size="md" color="var(--text-primary)">
            {children ||
              'An error occurred while generating your summary. Please try again.'}
          </Text>
        ) : markdown && typeof children === 'string' ? (
          <MarkdownContent
            content={children}
            enableCodeCopy={enableCodeCopy}
            customComponents={customComponents}
          />
        ) : (
          <Text size="md" color="var(--text-primary)">
            {children}
          </Text>
        )}

        {variant !== 'error' && showDisclaimer && (
          <Box display="flex" gap="var(--spacing-1)" alignItems="center">
            <Icon variant="AiMagic" size="large" />
            <Text size="xs" color="var(--text-primary)">
              This AI summary was generated from the property information from
              this package. This answer may display incorrect information.
            </Text>
          </Box>
        )}

        {variant === 'error' && (
          <div css={errorRetryStyles}>
            <Button
              variant="secondary"
              size="md"
              onClick={onErrorRetry}
              icon={<Icon variant="ArrowRotateLeft" />}
            >
              <Text size="sm" fontWeight="bold">
                Try Again
              </Text>
            </Button>
          </div>
        )}
      </div>

      {showHelpfulQuestion && variant !== 'error' && (
        <div css={footerStyles}>
          <Text size="md" color="var(--text-primary)">
            Was this helpful?
          </Text>
          <div css={actionsStyles}>
            <Button
              variant="text"
              size="xs"
              onClick={handleHelpfulYes}
              aria-pressed={visualHelpful === 'yes'}
            >
              <IconLabel
                variant={visualHelpful === 'yes' ? 'ThumbsUpSolid' : 'ThumbsUp'}
                label="Yes"
              />
            </Button>
            <Button
              variant="text"
              size="xs"
              onClick={handleHelpfulNo}
              aria-pressed={visualHelpful === 'no'}
            >
              <IconLabel
                variant={
                  visualHelpful === 'no' ? 'ThumbsDownSolid' : 'ThumbsDown'
                }
                label="No"
              />
            </Button>
          </div>
        </div>
      )}
    </Box>
  );
};

export default AIResponse;
